import { useTable, type RowData } from '@tanstack/react-table';
import { useState, type ReactNode } from 'react';
import { cn } from '../lib/utils';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { DataTableHeader } from './data-table-header';
import {
  dataTableFeatures,
  type DataTableColumn,
  type DataTableColumnMeta
} from './data-table-features';

interface DataTableProps<TData extends RowData> {
  columns: DataTableColumn<TData>[];
  data: TData[];
  /** Names the table's scroll region, e.g. "Data at Step 2 · Enrich". */
  label: string;
  /** Keeps only rows with a cell containing this text (case-insensitive). */
  filter?: string;
  /** Header clicks sort by that column. On by default. */
  sortable?: boolean;
  /** Shown in place of rows when there are none, or none match `filter`. */
  emptyMessage?: ReactNode;
  getRowId?: (row: TData, index: number) => string;
  className?: string;
}

const CHANGE_HEAD: Record<NonNullable<DataTableColumnMeta['change']>, string> = {
  added: 'bg-[color-mix(in_srgb,var(--z-brand)_6%,var(--z-surface-solid))]',
  retyped: 'bg-[color-mix(in_srgb,var(--z-warning)_8%,var(--z-surface-solid))]'
};
const CHANGE_CELL: Record<NonNullable<DataTableColumnMeta['change']>, string> = {
  added: 'bg-brand-subtle',
  retyped: 'bg-warning-subtle'
};

const ARIA_SORT = { asc: 'ascending', desc: 'descending' } as const;

/**
 * Rows under typed, sortable headers, on TanStack Table v9: the header stays
 * pinned while the body scrolls both ways. Columns carry their type and any
 * change tint in `meta`; build them with `createDataTableColumns`.
 *
 * Generic over the row type, so it is a plain arrow function rather than an
 * `FC` const: `FC` cannot carry a type parameter.
 */
export const DataTable = <TData extends RowData>({
  columns,
  data,
  label,
  filter = '',
  sortable = true,
  emptyMessage = 'No rows',
  getRowId,
  className
}: DataTableProps<TData>) => {
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);
  const table = useTable({
    features: dataTableFeatures,
    columns,
    data,
    getRowId,
    state: { sorting, globalFilter: filter },
    onSortingChange: setSorting,
    globalFilterFn: 'includesString',
    enableSorting: sortable
  });
  const rows = table.getRowModel().rows;

  return (
    // Focusable, so the region scrolls by keyboard even with nothing focusable inside.
    <div
      tabIndex={0}
      role="region"
      aria-label={label}
      className={cn(
        'overflow-auto outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        className
      )}
    >
      <table className="w-max min-w-full border-separate border-spacing-0 text-left text-body-sm">
        <TableHeader className="[&_tr]:border-b-0">
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className="hover:bg-transparent">
              {group.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                const sorted = header.column.getIsSorted();
                return (
                  <TableHead
                    key={header.id}
                    aria-sort={sorted ? ARIA_SORT[sorted] : undefined}
                    className={cn(
                      'sticky top-0 z-1 h-auto border-b border-border bg-[var(--z-surface-solid)] px-3 py-2 align-bottom font-normal',
                      meta?.change && CHANGE_HEAD[meta.change],
                      meta?.align === 'right' && 'text-right'
                    )}
                  >
                    {header.isPlaceholder ? null : (
                      <DataTableHeader
                        meta={meta}
                        sorted={sorted}
                        canSort={header.column.getCanSort()}
                        onSort={header.column.getToggleSortingHandler()}
                      >
                        <table.FlexRender header={header} />
                      </DataTableHeader>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.map((row) => (
              <TableRow key={row.id} className="border-b-0">
                {row.getAllCells().map((cell) => {
                  const meta = cell.column.columnDef.meta;
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'max-w-[280px] truncate border-b border-border px-3 py-1.5 text-[color:var(--z-text-secondary)]',
                        meta?.mono && 'font-mono text-[12px]',
                        meta?.align === 'right' && 'text-right tabular-nums',
                        meta?.change && CHANGE_CELL[meta.change]
                      )}
                    >
                      <table.FlexRender cell={cell} />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={columns.length}
                className="px-3 py-6 text-center text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </table>
    </div>
  );
};
